/**
 * Tiny fetch wrapper for `/api/*` calls.
 *
 * - Base URL comes from `VITE_API_BASE_URL`; empty in both dev and production
 *   (dev relies on Vite's `/api` proxy; prod is same-origin under Spring Boot).
 * - Returns parsed JSON typed as `T`. Throws `ApiError` on non-2xx so callers
 *   (TanStack Query) can surface error state.
 * - No response unwrap — the API contract explicitly forbids `{ data: ... }`
 *   wrappers, so what fetch returns is what callers see.
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  constructor(message: string, status: number, url: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.url = url;
  }
}

export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    ...init,
  });
  if (!res.ok) {
    throw new ApiError(
      `GET ${path} failed: ${res.status} ${res.statusText}`,
      res.status,
      url,
    );
  }
  return (await res.json()) as T;
}
